/*
 * Copyright (c) 2016 Omnigon Communications, LLC. All rights reserved.
 *
 * This software is the confidential and proprietary information of Omnigon Communications, LLC
 * ("Confidential Information"). You shall not disclose such Confidential Information and shall access and use it only
 * in accordance with the terms of the license agreement you entered into with Omnigon Communications, LLC, its
 * subsidiaries, affiliates or authorized licensee. Unless required by applicable law or agreed to in writing, this
 * Confidential Information is provided on an "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either
 * express or implied. See the license agreement for the specific language governing permissions and limitations.
 */

import Component from 'can/component/component';
import helpers from '../../base/helpers';
import can from 'can';
import dtm from 'scripts/services/dtm';
import template from './video.stache';
import DomEventsManager from 'scripts/dom-events-manager';
import domUtils from 'scripts/utils/dom';
import deviceUtil from 'scripts/utils/device';
import appContext from 'scripts/app-context';

const pageType = window.globalSettings.pageType;
const inWebView = window.globalSettings.inWebView;

Component.extend({
    tag: 'card-video',
    template: template,
    leakScope: false,
    viewModel: {
        define: {
            card: {
                Type: can.Map,
                Value: '*',
            },
            activeCard: {
                Type: can.Map,
                Value: null,
            },
            isVideoPaused: {
                type: 'boolean',
                value: true,
            },
            isPlayedOnce: {
                type: 'boolean',
                value: false,
            },
            isAbleToAutoplay: {
                type: 'boolean',
                value: true,
            },
            autoPlayed: {
                type: 'boolean',
                value: false,
            },
            activeCardIndex: {
                type: 'number',
                value: null,
            },
            cardIndex: {
                type: 'number',
                value: null,
            },
            isActiveCard: {
                type: 'boolean',
                get() {
                    return (
                        this.attr('cardIndex') == this.attr('activeCardIndex')
                    );
                },
            },
            analyticsVideoSource: {
                type: 'string',
                get() {
                    const postFix = this.attr('autoPlayed') ? ' Auto' : '';

                    switch (pageType) {
                        case 'player-detail':
                            return `Player Latest Feed Inline${postFix}`;
                        case 'latest':
                            return `Home Latest Feed Inline${postFix}`;
                        default:
                            return '';
                    }
                },
            },
            analyticsCardType: {
                type: 'string',
                get() {
                    const postFix = this.attr('autoPlayed') ? 'Auto' : 'Inline';

                    switch (pageType) {
                        case 'latest':
                            return `Video ${postFix}`;
                        default:
                            return '';
                    }
                },
            },
            isMobile: {
                type: 'boolean',
                get() {
                    return (
                        ['extra-small'].indexOf(
                            appContext.model.screen.attr('screenSize'),
                        ) > -1
                    );
                },
            },
            isMobileOrTablet: {
                type: 'boolean',
                get() {
                    return deviceUtil.isMobile || deviceUtil.isTablet;
                },
            },
            isPlayOnReady: {
                type: 'boolean',
                value: false,
            },
        },
        toggleVideoPlay() {
            const player = this.attr('player');
            if (!Boolean(player)) {
                return;
            }

            if (player.isPlaying()) {
                player.pause();
            } else {
                player.play();
            }
        },
    },
    helpers,
    events: {
        inserted() {
            this.element
                .find('brightcove-video')
                .viewModel()
                .on('brightcoveready', this.onVideoReady.bind(this));

            appContext.model.bind('card-video-play', (_, videoId) => {
                const cardVideoId = this.viewModel.attr('card.identifier');

                if (videoId !== cardVideoId) {
                    if (!this.viewModel.attr('isVideoPaused')) {
                        this.viewModel.toggleVideoPlay();
                    }
                }
            });

            if (!this.viewModel.attr('isMobile')) {
                DomEventsManager.subscribe(this, false);
            }
        },

        removed: function() {
            if (!this.viewModel.attr('isMobile')) {
                DomEventsManager.unsubscribe(this);
            }
        },

        checkFeaturedVideo() {
            if (domUtils.isOnScreen(this.element, true, true)) {
                if (
                    this.viewModel.attr('isAbleToAutoplay') &&
                    this.viewModel.card.isFeatured
                ) {
                    let activeCard = this.viewModel.attr('activeCard');
                    if (
                        !activeCard ||
                        !activeCard.viewModel ||
                        activeCard.viewModel.card.isFeatured
                    ) {
                        this.viewModel.player.play();

                        if (!this.viewModel.attr('autoPlayed')) {
                            this.viewModel.attr('autoPlayed', true);
                        }
                    }
                }
            } else {
                if (
                    this.viewModel.attr('isActiveCard') &&
                    !domUtils.isOnScreen(this.element, true)
                ) {
                    this.viewModel.attr('activeCardIndex', null);
                }
            }
        },

        onWindowScroll() {
            this.checkFeaturedVideo();
        },

        onVideoReady(type, payload) {
            this.viewModel.attr('player', payload);
            this.viewModel.player.on(
                'brightcoveplay',
                this.onVideoPlay.bind(this),
            );
            this.viewModel.player.on(
                'videopause',
                this.onVideoPaused.bind(this),
            );
            this.checkFeaturedVideo();

            const isPlayOnReady = this.viewModel.attr('isPlayOnReady');

            if (isPlayOnReady) {
                payload.play();
            }
        },

        onVideoPlay(type, payload) {
            payload.player.userActive(true);
            this.viewModel.attr(
                'activeCardIndex',
                this.viewModel.attr('cardIndex'),
            );
            this.viewModel.attr('isVideoPaused', false);
            if (!this.viewModel.attr('isPlayedOnce')) {
                this.viewModel.attr('isPlayedOnce', true);
            }

            const videoId = this.viewModel.attr('card.identifier');
            appContext.model.dispatch('card-video-play', [videoId]);
        },

        onVideoPaused() {
            if (
                !this.viewModel.player.player.scrubbing() &&
                !this.viewModel.player.player.isFullscreen()
            ) {
                this.viewModel.attr('isVideoPaused', true);
            }
        },

        '.js-card-selector click'(element) {
            let player = this.element.find('brightcove-video').viewModel();
            this.viewModel.attr('activeCard', this);
            this.viewModel.attr(
                'activeCardIndex',
                this.viewModel.attr('cardIndex'),
            );

            const vmPlayer = this.viewModel.attr('player');
            if (vmPlayer) {
                vmPlayer.play();
            } else {
                player.on('brightcoveready', this.onVideoReady.bind(this));
                this.viewModel.attr('isPlayOnReady', true);
            }
        },

        '.js-track-video-launch click'() {
            let preFix;

            switch (pageType) {
                case 'player-detail':
                    preFix = 'Player';
                case 'latest':
                    preFix = 'Home';
                default:
                    preFix = '';
            }
            dtm.trackVideoLaunch(
                this.viewModel.attr('card.title'),
                `${preFix} Latest Feed Link`,
            );
        },

        '{scope} activeCardIndex'(scope, event, activeCardIndex) {
            if (activeCardIndex != this.viewModel.attr('cardIndex')) {
                let player = this.viewModel.player;
                if (
                    player &&
                    player.isPlaying() &&
                    !player.player.isFullscreen()
                ) {
                    player.pause();
                    this.viewModel.attr('isAbleToAutoplay', false);
                }
            }
        },

        'video click'() {
            this.viewModel.attr('isAbleToAutoplay', false);
        },

        '.vjs-playing click'() {
            this.viewModel.attr('isAbleToAutoplay', false);
        },

        '.js-overlay click'() {
            this.viewModel.toggleVideoPlay();
        },

        dtmTrack(el) {
            const title = $(el)
                .closest('.js-analytics-video')
                .attr('card-title');
            if (title) {
                dtm.trackFeaturedVideo(title);
            }
        },

        '.s-btn--play click'(el) {
            this.viewModel.toggleVideoPlay();
            this.dtmTrack(el);
        },

        '.js-analytics-video-page click'(el) {
            this.dtmTrack(el);
        },
    },
});
